"use client";

import { ReactNode } from "react";
import { Typography } from "./typography";
import { cn } from "@drpcrd/utils/shared";

type SelectableCardProps = {
  isSelected: boolean;
  title: string;
  description?: string;
  leftElement?: ReactNode;
  rightElement?: ReactNode;
  statusBadge?: ReactNode;
  footer?: ReactNode;
  onSelect?: () => void;
  disabled?: boolean;
};

export const SelectableCard = ({
  isSelected,
  title,
  description,
  leftElement,
  rightElement,
  statusBadge,
  footer,
  onSelect,
  disabled = false,
}: SelectableCardProps) => {
  const handleClick = () => {
    if (!disabled && onSelect) {
      onSelect();
    }
  };

  return (
    <div
      className={cn(
        "border-border-primary flex h-fit w-full flex-col gap-2 overflow-hidden rounded-lg border bg-white p-2 transition-all",
        isSelected && "border-black",
        disabled ? "cursor-not-allowed" : "hover:cursor-pointer",
      )}
      onClick={handleClick}
      role="button"
      tabIndex={disabled ? -1 : 0}
      title={title}
      aria-pressed={isSelected}
      aria-disabled={disabled}
      onKeyDown={(e) => {
        if ((e.key === "Enter" || e.key === " ") && !disabled) {
          handleClick();
          e.preventDefault();
        }
      }}
    >
      {/* Top section: leftElement and text content side by side */}
      <div className="flex items-center gap-2 overflow-hidden">
        {leftElement && (
          <div className="flex h-full items-start">{leftElement}</div>
        )}

        <div className="flex h-fit flex-col">
          <Typography truncate size="sm" weight="bold" className="leading-2.75">
            {title}
          </Typography>
          {description && (
            <Typography
              size="sm"
              color="neutral"
              className="line-clamp-2 leading-2.75"
            >
              {description}
            </Typography>
          )}
        </div>

        {rightElement && <div className="ml-auto">{rightElement}</div>}
      </div>

      {footer && (
        <div className="flex flex-col gap-3">
          <div className="h-px w-full bg-gray-200" />
          <div onClick={(e) => e.stopPropagation()}>{footer}</div>
        </div>
      )}

      {/* Bottom section: status badge spanning full width */}
      {statusBadge && <div className="flex justify-start">{statusBadge}</div>}
    </div>
  );
};
